// Cache de resultados da Places API (Google Maps).
//
// POR QUE EXISTE
// As buscas de lugares — Nearby Search (cards de sugestão), Text Search
// (foto/nota de hotel, busca no mapa) e Place Details — são os SKUs MAIS
// CAROS do Google Maps (~4-5x o Directions). O resultado de "lugares perto
// de X" ou "detalhes do lugar Y" é praticamente estático, então cache
// resolve quase tudo.
//
// Mesma arquitetura do directionsCache.js: duas camadas.
//   1. Memória (o Map abaixo) — instantâneo, vive enquanto a aba está aberta.
//   2. KV (Cloudflare, via /api/places/cache) — TTL 120 dias, compartilhado
//      entre sessões/dispositivos/usuários. A chave não tem nada de usuário
//      nem de viagem, então o cache é COMUM a todo mundo.
//
// Diferente do Directions (que pré-carrega tudo no mount), aqui as buscas
// são sob demanda (clicar num chip, abrir um popup). Então o fluxo é
// get-on-miss: memória → KV → API do Google, com write-through ao final.
//
// TESTABILIDADE
// As funções puras (key builders, isFresh, mem*) não importam nada de
// browser/firebase no topo, pra rodarem em Node nos specs. cacheGet/cacheSet
// fazem import dinâmico do authFetch — só executam no browser.

// 120 dias. Mesma decisão de TTL do directionsCache.
export const PLACES_TTL_MS = 120 * 24 * 60 * 60 * 1000;
export const PLACES_TTL_SECONDS = 120 * 24 * 60 * 60;

// Versão das chaves que carregam FOTO (nearby/text/details). Bumpar isso faz
// o cache ignorar TODAS as entradas antigas de uma vez — usado quando o que
// está cacheado ficou inválido. Motivo do v2: até a v1, o photoUrl (URL de
// foto do Google, cuja referência EXPIRA) era gravado junto e quebrava (403)
// depois de um tempo. Da v2 em diante a foto é resolvida fresca no cliente
// (ver resolvePlacePhotoUrl em places.js); as entradas v1 ficam órfãs e
// expiram sozinhas pelo TTL. Geocode NÃO é versionado (não tem foto).
const CACHE_VERSION = 'v2';

// 3 casas decimais (~110 m) — para buscas "perto desta área" é a granularidade
// certa: agrupa requests do mesmo bairro na mesma entrada sem fragmentar.
const COORD_DECIMALS = 3;

function _round(n) {
  return Number(n).toFixed(COORD_DECIMALS);
}

function _loc(center) {
  if (!center) return '';
  const lat = Number(center.lat), lng = Number(center.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
  return `${_round(lat)},${_round(lng)}`;
}

/**
 * Chave de cache para uma Nearby Search.
 * @param {string} kind   chip ('hotels' | 'restaurants' | 'attractions')
 * @param {{lat,lng}} center
 * @param {number} radius
 * @param {number} limit
 */
export function nearbyCacheKey(kind, center, radius, limit) {
  const loc = _loc(center);
  if (!kind || !loc) return null;
  return `${CACHE_VERSION}:near:${kind}:${loc}:${Math.round(Number(radius) || 0)}:${Math.round(Number(limit) || 0)}`;
}

/**
 * Chave de cache para uma Text Search. A query é normalizada (minúsculas,
 * espaços colapsados) pra que variações triviais batam na mesma entrada.
 * `count` (maxResults) entra na chave porque "top 1" e "top 4" da mesma
 * query são respostas diferentes — sem isso um caller degradaria o outro.
 */
export function textCacheKey(query, center, count) {
  const q = String(query || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!q) return null;
  const loc = _loc(center);
  const n = Math.round(Number(count) || 0);
  return `${CACHE_VERSION}:txt:${q}${loc ? '@' + loc : ''}${n ? '#' + n : ''}`;
}

/** Chave de cache para Place Details — o placeId é estável pra sempre. */
export function detailsCacheKey(placeId) {
  const id = String(placeId || '').trim();
  return id ? `${CACHE_VERSION}:det:${id}` : null;
}

/**
 * Chave de cache para reverse-geocode "ponto → centro da cidade". Arredonda a
 * 3 casas (~110 m): hotéis/itens no mesmo quarteirão caem na mesma cidade, e
 * 110 m não atravessa fronteira de cidade na prática. Reverse-geocode é SKU
 * pago, então cacheamos (memória + KV) pra não geocodificar a mesma cidade a
 * cada render/sessão.
 */
export function geocodeCacheKey(lat, lng) {
  const a = Number(lat), b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return `geo:${_round(a)},${_round(b)}`;
}

/**
 * Chave de cache para forward-geocode "texto (endereço/cidade) → centro da
 * cidade". Normaliza a query (minúsculas, espaços colapsados) pra variações
 * triviais baterem na mesma entrada. Forward-geocode é SKU pago → cacheia.
 */
export function geocodeQueryCacheKey(query) {
  const q = String(query || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return q ? `geoq:${q}` : null;
}

/**
 * Uma entrada de cache ainda é válida? Precisa existir, ter status 'OK',
 * timestamp numérico e estar dentro do TTL.
 */
export function isFresh(entry, now = Date.now(), maxAgeMs = PLACES_TTL_MS) {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.status !== 'OK') return false;
  const ts = Number(entry.ts);
  if (!Number.isFinite(ts)) return false;
  return (now - ts) < maxAgeMs;
}

// ── Camada 1: cache em memória ───────────────────────────────────────────────
const _mem = new Map();

/** Lê do cache em memória. Retorna a entrada, ou null em miss/expirada. */
export function memGet(key) {
  if (!key) return null;
  const entry = _mem.get(key);
  if (!isFresh(entry)) {
    if (entry) _mem.delete(key);
    return null;
  }
  return entry;
}

/** Grava no cache em memória (só entradas válidas). */
export function memSet(key, entry) {
  if (!key || !isFresh(entry)) return;
  _mem.set(key, entry);
}

/** Diagnóstico — número de entradas no cache em memória. */
export function memSize() {
  return _mem.size;
}

/** Apenas para testes — zera o cache em memória. */
export function _resetMemCache() {
  _mem.clear();
}

// ── Camada 2: cache persistente (KV, via backend) ────────────────────────────

async function _authFetch(url, init) {
  // Import dinâmico: mantém o topo do módulo livre de firebase pra os specs.
  const { authFetch } = await import('../../core/auth.js');
  return authFetch(url, init);
}

/**
 * Busca uma entrada: memória → KV. Retorna o `data` cacheado, ou null em miss.
 * Fail-open: se o backend falhar, devolve null e o chamador chama a API.
 *
 * @param {string} key
 * @returns {Promise<any|null>}
 */
export async function cacheGet(key) {
  if (!key) return null;
  const mem = memGet(key);
  if (mem) return mem.data;
  try {
    const res = await _authFetch('/api/places/cache?key=' + encodeURIComponent(key));
    if (!res.ok) return null;
    const json = await res.json();
    const entry = json?.entry;
    if (isFresh(entry)) {
      memSet(key, entry);
      return entry.data;
    }
  } catch (e) {
    console.warn('[placesCache] cacheGet falhou:', e?.message || e);
  }
  return null;
}

/**
 * Grava `data` no cache (memória + KV). O write no KV é fire-and-forget —
 * uma falha não afeta o resultado já entregue ao usuário.
 *
 * @param {string} key
 * @param {any} data  Resultado a cachear (lista de lugares, ou um lugar).
 */
export function cacheSet(key, data) {
  if (!key || data == null) return;
  const entry = { status: 'OK', ts: Date.now(), data };
  memSet(key, entry);
  _authFetch('/api/places/cache', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, entry }),
  }).catch((e) => console.warn('[placesCache] cacheSet falhou:', e?.message || e));
}
